import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { imagineStore } from "@/stores/imagine.store";
import { useEffect, useState } from "react";
import { VariablesList } from "./environment-variables-form";
import { useFetchProjectVariables } from "@/hooks/use-fetch-project-variables";
import { useSaveProjectVariablesMutation } from "@/hooks/use-save-project-variables-mutation";
import { toast } from "sonner";

export function EnvironmentVariablesDialogTrigger({
  children,
  onFetchingVariablesChange,
  onOpenSuccess,
}: {
  children: React.ReactNode;
  onFetchingVariablesChange: (isFetching: boolean) => void;
  onOpenSuccess?: () => void;
}) {
  const projectId = imagineStore.use.projectId();
  const [open, setOpen] = useState(false);
  const fetchVariablesQuery = useFetchProjectVariables({ enabled: open });
  const saveVariablesMutation = useSaveProjectVariablesMutation({
    projectId,
    onSuccess: () => {
      toast.success("Variables saved successfully");
      setOpen(false);
    },
    onError: (error) => {
      toast.error(error?.message ?? "Failed to save variables", {
        duration: 5000,
      });
    },
  });

  const handleOpenChange = async (open: boolean) => {
    if (open === true) {
      const result = await fetchVariablesQuery.refetch();
      if (result.error) {
        toast.error(
          result.error?.message ?? "Failed to fetch project variables",
          {
            duration: 5000,
          },
        );
      } else {
        setOpen(true);
        onOpenSuccess?.();
      }
    } else {
      setOpen(open);
    }
  };

  useEffect(() => {
    onFetchingVariablesChange(fetchVariablesQuery.isFetching);
  }, [fetchVariablesQuery.isFetching]);

  return (
    <Dialog
      open={open && Boolean(fetchVariablesQuery.data)}
      onOpenChange={handleOpenChange}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        {fetchVariablesQuery.data && !fetchVariablesQuery.error && (
          <>
            <DialogHeader className="mb-2">
              <DialogTitle>Environment Variables</DialogTitle>
              <DialogDescription asChild>
                <div>
                  <div>
                    Configure the variables that will be available in your
                    project.
                  </div>
                  <div>
                    For a variable to be available in your front-end, it must
                    start with <code>VITE_PUBLIC_</code>.
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              {saveVariablesMutation.error && (
                <div className="text-destructive">
                  {saveVariablesMutation.error.message ??
                    "Failed to save variables"}
                </div>
              )}
              <VariablesList
                initialVariables={fetchVariablesQuery.data?.variables || []}
                managedVariables={
                  fetchVariablesQuery.data?.managedVariables || []
                }
                onSave={async (variables) => {
                  const { variables: patchedVariables } =
                    await saveVariablesMutation.mutateAsync({ variables });
                  return patchedVariables;
                }}
                isSaving={saveVariablesMutation.isPending}
              />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
